import {
  Attachment,
  AttachmentModel,
} from '@digital-platform/attachments/domain';
import { DomainModelHelper } from '@digital-platform/shared/domain';
import { RegulatoryQuestion } from '../interfaces';

export class RegulatoryQuestionModel implements RegulatoryQuestion {
  readonly id:
    | 'rcra1'
    | 'rcra2'
    | 'rcra3'
    | 'rcra4'
    | 'rcra5'
    | 'rcra6'
    | 'rcra7'
    | 'rcra8'
    | 'rcra9'
    | 'rcra10'
    | 'rep1'
    | 'rep2'
    | 'rep3'
    | 'rep4';
  readonly questionText: string;
  readonly response: string;
  readonly alreadyUploaded?: boolean;
  readonly attachment?: Attachment;
  readonly applicableWasteCodesAndAgents?: string;
  readonly regulatorCitation?: string;
  readonly description?: string;
  readonly type?: string;

  public static DEFAULT_CONFIG: RegulatoryQuestion = {
    id: null,
    questionText: '',
    response: null,
  };

  public constructor(config?: RegulatoryQuestion) {
    // this can only be done on root objects, child objects will end up being
    // overwritten (if they extend the DEFAULT_CONFIG)
    Object.assign(this, RegulatoryQuestionModel.DEFAULT_CONFIG);

    this.id = this.convertToId(config?.id);
    this.questionText =
      DomainModelHelper.ensureDataIsString(config?.questionText) || '';
    this.response =
      DomainModelHelper.ensureDataIsString(config?.response) || null;

    if (this.id === 'rcra5') {
      const type = DomainModelHelper.ensureDataIsString(config?.type);

      if (type !== 'delisted') {
        throw new TypeError(
          `Invalid Type '${type}' for Regulatory Question ${this.id}, must be 'delisted'`
        );
      }

      this.type = type;
      this.alreadyUploaded = DomainModelHelper.ensureDataIsBoolean(
        config?.alreadyUploaded
      );
      this.attachment = config?.attachment
        ? new AttachmentModel(config?.attachment)
        : null;
    }
    if (this.id === 'rcra6') {
      const type = DomainModelHelper.ensureDataIsString(config?.type);

      if (type !== 'hazardousCharacteristics') {
        throw new TypeError(
          `Invalid Type '${type}' for Regulatory Question ${this.id}, must be 'hazardousCharacteristics'`
        );
      }

      this.type = type;
      this.applicableWasteCodesAndAgents = DomainModelHelper.ensureDataIsString(
        config?.applicableWasteCodesAndAgents
      );
    }
    if (this.id === 'rcra10') {
      const type = DomainModelHelper.ensureDataIsString(config?.type);

      if (type !== 'hazardousRegulatoryCitation') {
        throw new TypeError(
          `Invalid Type '${type}' for Regulatory Question ${this.id}, must be 'hazardousRegulatoryCitation'`
        );
      }

      this.type = type;
      this.regulatorCitation = DomainModelHelper.ensureDataIsString(
        config?.regulatorCitation
      );
    }
    if (this.id === 'rep4') {
      const type = DomainModelHelper.ensureDataIsString(config?.type);

      if (type !== 'tsdLike') {
        throw new TypeError(
          `Invalid Type '${type}' for Regulatory Question ${this.id}, must be 'tsdLike'`
        );
      }

      this.type = type;
      this.description =
        DomainModelHelper.ensureDataIsString(config?.description) || '';
      this.attachment = config?.attachment
        ? new AttachmentModel(config?.attachment)
        : null;
    }
  }

  protected convertToId(data: string): RegulatoryQuestion['id'] {
    const converted = DomainModelHelper.ensureDataIsString(data);

    const validIds = [
      'rcra1',
      'rcra2',
      'rcra3',
      'rcra4',
      'rcra5',
      'rcra6',
      'rcra7',
      'rcra8',
      'rcra9',
      'rcra10',
      'rep1',
      'rep2',
      'rep3',
      'rep4',
    ];

    if (validIds.includes(converted)) {
      return converted as RegulatoryQuestion['id'];
    }

    throw new TypeError(`Invalid Regulatory Question ID '${converted}'!`);
  }
}
